import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { fetchClassesInMySchool, type ClassroomInSchool } from '../../lib/classroomsApi';

type DocItem = {
  id: number;
  scope_type: 'school' | 'class' | string;
  classroom_id?: number | null;
  classroom_name?: string | null;
  title: string | null;
  original_name: string;
  mime_type: string | null;
  size: number | null;
  created_at: string | null;
  uploaded_by?: { id: number | null; name: string | null } | null;
};

function bytesLabel(n: number | null | undefined) {
  const v = Number(n || 0);
  if (!Number.isFinite(v) || v <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let x = v;
  let i = 0;
  while (x >= 1024 && i < units.length - 1) {
    x /= 1024;
    i += 1;
  }
  return `${Math.round(x * 10) / 10} ${units[i]}`;
}

function dateLabel(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ro-RO');
  } catch {
    return iso;
  }
}

function filenameFromHeader(header: string | undefined, fallback: string) {
  const h = String(header || '');
  const m = /filename=\"?([^\";]+)\"?/i.exec(h);
  return (m?.[1] || '').trim() || fallback;
}

export default function DocumentsPage() {
  const navigate = useNavigate();
  const { me, uiRole, signOut } = useAuth();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<DocItem[]>([]);
  const [q, setQ] = useState('');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'school' | 'class'>('all');

  const canUpload = useMemo(() => {
    const r = (me?.role || '').toString().toLowerCase();
    return r === 'profesor' || r === 'admin' || r === 'secretariat';
  }, [me?.role]);

  const headerUser = useMemo(() => {
    const r = (uiRole || 'student') as any;
    return { role: r, name: me?.full_name || 'Utilizator', avatar: me?.avatar_url || undefined };
  }, [me?.avatar_url, me?.full_name, uiRole]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get('/api/documents/school');
      const list = Array.isArray((resp.data as any)?.items) ? ((resp.data as any).items as DocItem[]) : [];
      setItems(list);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut încărca documentele.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const needle = String(q || '').trim().toLowerCase();
    let list = items.slice();
    if (scopeFilter !== 'all') {
      list = list.filter((d) => String(d.scope_type || 'school') === scopeFilter);
    }
    if (!needle) return list;
    return list.filter((d) => {
      const t = (d.title || '').toString().toLowerCase();
      const f = (d.original_name || '').toString().toLowerCase();
      return t.includes(needle) || f.includes(needle);
    });
  }, [items, q, scopeFilter]);

  const doDownload = async (doc: DocItem) => {
    try {
      const resp = await api.get(`/api/documents/school/${encodeURIComponent(String(doc.id))}/download`, {
        responseType: 'blob' as any,
      });
      const blob = new Blob([resp.data], { type: (resp.headers as any)?.['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filenameFromHeader((resp.headers as any)?.['content-disposition'], doc.original_name || `document_${doc.id}`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut descărca documentul.');
    }
  };

  const doDelete = async (doc: DocItem) => {
    const ok = window.confirm(`Ștergi documentul "${doc.title || doc.original_name}"?`);
    if (!ok) return;
    try {
      await api.delete(`/api/documents/school/${encodeURIComponent(String(doc.id))}`);
      setItems((prev) => prev.filter((x) => x.id !== doc.id));
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut șterge documentul.');
    }
  };

  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadScope, setUploadScope] = useState<'school' | 'class'>('school');
  const [uploadClassroomId, setUploadClassroomId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [myClasses, setMyClasses] = useState<ClassroomInSchool[]>([]);

  useEffect(() => {
    if (!canUpload) return;
    void (async () => {
      try {
        const cls = await fetchClassesInMySchool();
        setMyClasses(cls || []);
      } catch {
        setMyClasses([]);
      }
    })();
  }, [canUpload]);

  const uploadClassOptions = useMemo(() => {
    const r = (me?.role || '').toString().toLowerCase();
    const list =
      r === 'profesor'
        ? (myClasses || []).filter((c) => Boolean(c.is_admin))
        : (myClasses || []);
    return list.map((c) => ({ value: Number(c.id), label: c.name }));
  }, [me?.role, myClasses]);

  const doUpload = async () => {
    if (!canUpload) return;
    if (!uploadFile) {
      alert('Selectează un fișier.');
      return;
    }
    if (uploadScope === 'class' && !uploadClassroomId) {
      alert('Selectează clasa.');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      if (uploadTitle.trim()) form.append('title', uploadTitle.trim());
      form.append('scope_type', uploadScope);
      if (uploadScope === 'class' && uploadClassroomId) form.append('classroom_id', String(uploadClassroomId));
      form.append('file', uploadFile, uploadFile.name);
      await api.post('/api/documents/school/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadTitle('');
      setUploadFile(null);
      setUploadScope('school');
      setUploadClassroomId(null);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut încărca documentul.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Documente - Scola</title>
      </Helmet>

      <Header
        user={headerUser}
        onNavigate={(p) => navigate(p)}
        onSignOut={() => {
          signOut();
          navigate('/authentication-portal');
        }}
      />

      <main className="pt-16">
        <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Documente</h1>
              <p className="text-sm text-muted-foreground">
                Documente utile pentru toți. {canUpload ? 'Poți încărca și administra documente.' : 'Poți doar descărca.'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" iconName="RefreshCw" disabled={loading || uploading} onClick={() => void load()}>
                Reîncarcă
              </Button>
            </div>
          </div>

          {canUpload ? (
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="font-semibold text-foreground mb-3">Încarcă document</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <Input
                  label="Titlu (opțional)"
                  placeholder="Ex: Cerere tip, regulament, program..."
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                />
                <div className="grid grid-cols-1 gap-3">
                  <Select
                    label="Destinație"
                    value={uploadScope}
                    options={[
                      { value: 'school', label: 'Școală' },
                      { value: 'class', label: 'Clasă' },
                    ]}
                    onChange={(v) => {
                      const next = String(v) === 'class' ? 'class' : 'school';
                      setUploadScope(next);
                      if (next !== 'class') setUploadClassroomId(null);
                    }}
                  />
                  {uploadScope === 'class' ? (
                    <Select
                      label="Clasă"
                      value={uploadClassroomId ?? ''}
                      options={uploadClassOptions as any}
                      onChange={(v) => setUploadClassroomId(v ? Number(v) : null)}
                      searchable
                      clearable
                    />
                  ) : null}
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">Fișier</div>
                  <input
                    type="file"
                    className="mt-1 block w-full text-sm text-foreground"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  />
                  <div className="text-xs text-muted-foreground mt-1">
                    Formate acceptate: pdf, docx, xlsx, pptx, txt, rtf, jpg/png/webp, zip.
                  </div>
                </div>
                <Button
                  variant="default"
                  iconName="Upload"
                  disabled={uploading || loading}
                  loading={uploading}
                  onClick={() => void doUpload()}
                >
                  Încarcă
                </Button>
              </div>
            </div>
          ) : null}

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-semibold text-foreground">Lista documente</div>
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex items-center gap-2">
                  {[
                    { id: 'all' as const, label: 'Toate' },
                    { id: 'school' as const, label: 'Școală' },
                    { id: 'class' as const, label: 'Clasă' },
                  ].map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setScopeFilter(t.id)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium border ${
                        scopeFilter === t.id ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-foreground'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <div className="w-full sm:w-80">
                  <Input
                    label="Caută"
                    placeholder="Titlu / nume fișier..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="text-sm text-muted-foreground mt-4">{loading ? 'Se încarcă…' : 'Nu există documente.'}</div>
            ) : (
              <div className="mt-4 divide-y divide-border">
                {filtered.map((d) => (
                  <div key={d.id} className="py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-foreground truncate">{d.title || d.original_name}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-1">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-border bg-muted/40">
                          <Icon name={String(d.scope_type || 'school') === 'class' ? 'Users' : 'School'} size={12} />
                          <span>
                            {String(d.scope_type || 'school') === 'class'
                              ? `Clasă: ${d.classroom_name || '—'}`
                              : 'Școală'}
                          </span>
                        </span>
                        <span className="font-mono">{d.original_name}</span>
                        <span>•</span>
                        <span>{bytesLabel(d.size)}</span>
                        <span>•</span>
                        <span>{dateLabel(d.created_at)}</span>
                        {d.uploaded_by?.name ? (
                          <>
                            <span>•</span>
                            <span>încărcat de {d.uploaded_by.name}</span>
                          </>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button variant="outline" size="sm" iconName="Download" onClick={() => void doDownload(d)}>
                        Descarcă
                      </Button>
                      {canUpload ? (
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Trash2"
                          className="border-error/30 text-error hover:bg-error/10 hover:text-error"
                          onClick={() => void doDelete(d)}
                        >
                          Șterge
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
